import React, { ReactNode } from "react";
import { CoreUiRoutePath } from "./_coreui.route-path";

const CoreUiDashboard = React.lazy(
  () => import("../features/dashboard/CoreUiDashboard"),
);
const Colors = React.lazy(() => import("../features/theme/colors/Colors"));
const Typography = React.lazy(
  () => import("../features/theme/typography/Typography"),
);

// Base
const Cards = React.lazy(() => import("../features/base/cards/Cards"));
const Accordion = React.lazy(
  () => import("../features/base/accordion/Accordion"),
);
const Breadcrumbs = React.lazy(
  () => import("../features/base/breadcrumbs/Breadcrumbs"),
);
const Carousels = React.lazy(
  () => import("../features/base/carousels/Carousels"),
);
const Collapses = React.lazy(
  () => import("../features/base/collapses/Collapses"),
);
const ListGroups = React.lazy(
  () => import("../features/base/list-groups/ListGroups"),
);
const Navs = React.lazy(() => import("../features/base/navs/Navs"));
const Paginations = React.lazy(
  () => import("../features/base/paginations/Paginations"),
);
const Placeholders = React.lazy(
  () => import("../features/base/placeholders/Placeholders"),
);
const Popovers = React.lazy(() => import("../features/base/popovers/Popovers"));
const Progress = React.lazy(() => import("../features/base/progress/Progress"));
const Tables = React.lazy(() => import("../features/base/tables/Tables"));
const Tooltips = React.lazy(() => import("../features/base/tooltips/Tooltips"));
const Spinners = React.lazy(() => import("../features/base/spinners/Spinners"));

// Buttons
const LoadingButtons = React.lazy(
  () => import("../features/buttons/loading-buttons/LoadingButtons"),
);
const Dropdowns = React.lazy(
  () => import("../features/buttons/dropdowns/Dropdowns"),
);
const Buttons = React.lazy(() => import("../features/buttons/buttons/Buttons"));
const ButtonGroups = React.lazy(
  () => import("../features/buttons/button-groups/ButtonGroups"),
);

const FormControl = React.lazy(
  () => import("../features/forms/form-control/FormControl"),
);
const Select = React.lazy(
  () => import("../features/forms/input-group/InputGroup"),
);
const MultiSelect = React.lazy(
  () => import("../features/forms/multi-select/MultiSelect"),
);
const ChecksRadios = React.lazy(
  () => import("../features/forms/checks-radios/ChecksRadios"),
);
const FloatingLabels = React.lazy(
  () => import("../features/forms/floating-labels/FloatingLabels"),
);
const DatePicker = React.lazy(
  () => import("../features/forms/date-picker/DatePicker"),
);
const DateRangePicker = React.lazy(
  () => import("../features/forms/date-range-picker/DateRangePicker"),
);
const TimePicker = React.lazy(
  () => import("../features/forms/time-picker/TimePicker"),
);
const Layout = React.lazy(() => import("../features/forms/layout/Layout"));
const Validation = React.lazy(
  () => import("../features/forms/validation/Validation"),
);
const CoreUIIcons = React.lazy(
  () => import("../features/icons/coreui-icons/CoreUIIcons"),
);
const Brands = React.lazy(() => import("../features/icons/brands/Brands"));
const Flags = React.lazy(() => import("../features/icons/flags/Flags"));
const Alerts = React.lazy(
  () => import("../features/notifications/alerts/Alerts"),
);
const Badges = React.lazy(
  () => import("../features/notifications/badges/Badges"),
);
const Modals = React.lazy(
  () => import("../features/notifications/modals/Modals"),
);
const Toasts = React.lazy(
  () => import("../features/notifications/toasts/Toasts"),
);
const Calendar = React.lazy(
  () => import("../features/plugins/calendar/Calendar"),
);
const Charts = React.lazy(() => import("../features/plugins/charts/Charts"));
const GoogleMaps = React.lazy(
  () => import("../features/plugins/google-maps/GoogleMaps"),
);
const SmartTable = React.lazy(
  () => import("../features/smart-table/SmartTable"),
);
const Widgets = React.lazy(() => import("../widgets/Widgets"));
const Invoice = React.lazy(() => import("../features/apps/invoicing/Invoice"));
const InputGroup = React.lazy(
  () => import("../features/forms/input-group/InputGroup"),
);

const CoreUiNotFoundError = React.lazy(
  () => import("../features/errors/CoreUiNotFoundError"),
);
const CoreUiInternalServerError = React.lazy(
  () => import("../features/errors/CoreUiInternalServerError"),
);

const Range = React.lazy(() => import("../features/forms/range/Range"));

export type route = {
  component?: ReactNode;
  name?: string;
  path?: string;
  routes?: route[];
  paramIndex?: number;
  disabled?: undefined | boolean;
};

const coreuiRoutes = [
  { path: "coreui", name: "CoreUi" },
  {
    path: CoreUiRoutePath.Dashboard,
    name: "Dashboard",
    component: CoreUiDashboard,
  },
  { path: "theme", name: "Theme", component: Colors },
  { path: "theme/colors", name: "Colors", component: Colors },
  {
    path: "theme/typography",
    name: "Typography",
    component: Typography,
  },
  { path: "base", name: "Base", component: Cards },
  { path: "base/accordion", name: "Accordion", component: Accordion },
  { path: "base/breadcrumbs", name: "Breadcrumbs", component: Breadcrumbs },
  { path: "base/cards", name: "Cards", component: Cards },
  { path: "base/carousels", name: "Carousel", component: Carousels },
  { path: "base/collapses", name: "Collapse", component: Collapses },
  { path: "base/list-groups", name: "List Groups", component: ListGroups },
  { path: "base/navs", name: "Navs", component: Navs },
  { path: "base/paginations", name: "Paginations", component: Paginations },
  { path: "base/placeholders", name: "Placeholders", component: Placeholders },
  { path: "base/popovers", name: "Popovers", component: Popovers },
  { path: "base/progress", name: "Progress", component: Progress },
  { path: "base/spinners", name: "Spinners", component: Spinners },
  { path: "base/tables", name: "Tables", component: Tables },
  { path: "base/tooltips", name: "Tooltips", component: Tooltips },
  { path: "buttons", name: "Buttons", component: Buttons },
  { path: "buttons/buttons", name: "Buttons", component: Buttons },
  {
    path: "buttons/button-groups",
    name: "Button Groups",
    component: ButtonGroups,
  },
  {
    path: "buttons/loading-buttons",
    name: "Loading Buttons",
    component: LoadingButtons,
  },
  { path: "buttons/dropdowns", name: "Dropdowns", component: Dropdowns },
  { path: "forms", name: "Forms", component: FormControl },
  { path: "forms/form-control", name: "Form Control", component: FormControl },
  { path: "forms/select", name: "Select", component: Select },
  { path: "forms/multi-select", name: "Multi Select", component: MultiSelect },
  {
    path: "forms/checks-radios",
    name: "Checks & Radios",
    component: ChecksRadios,
  },
  { path: "forms/range", name: "Range", component: Range },
  { path: "forms/input-group", name: "Input Group", component: InputGroup },
  {
    path: "forms/floating-labels",
    name: "Floating Labels",
    component: FloatingLabels,
  },
  { path: "forms/date-picker", name: "Date Picker", component: DatePicker },
  {
    path: "forms/date-range-picker",
    name: "Date Range Picker",
    component: DateRangePicker,
  },
  { path: "forms/time-picker", name: "Time Picker", component: TimePicker },
  { path: "forms/layout", name: "Layout", component: Layout },
  { path: "forms/validation", name: "Validation", component: Validation },
  { path: "icons", name: "Icons", component: CoreUIIcons },
  { path: "icons/coreui-icons", name: "CoreUI Icons", component: CoreUIIcons },
  { path: "icons/flags", name: "Flags", component: Flags },
  { path: "icons/brands", name: "Brands", component: Brands },
  {
    path: "notifications",
    name: "Notifications",
    component: Alerts,
  },
  { path: "notifications/alerts", name: "Alerts", component: Alerts },
  { path: "notifications/badges", name: "Badges", component: Badges },
  { path: "notifications/modals", name: "Modals", component: Modals },
  { path: "notifications/toasts", name: "Toasts", component: Toasts },
  { path: "plugins", name: "Plugins", component: Calendar },
  { path: "plugins/calendar", name: "Calendar", component: Calendar },
  { path: "plugins/charts", name: "Charts", component: Charts },
  { path: "plugins/google-maps", name: "GoogleMaps", component: GoogleMaps },
  { path: "smart-table", name: "Smart Table", component: SmartTable },
  { path: "widgets", name: "Widgets", component: Widgets },
  { path: "apps", name: "Apps", component: Invoice },
  { path: "apps/invoicing", name: "Invoice", component: Invoice },
  { path: "apps/invoicing/invoice", name: "Invoice", component: Invoice },
  { path: "apps/email", name: "Email" },
  { path: "apps/email/inbox", name: "Inbox" },
  { path: "apps/email/compose", name: "Compose" },
  { path: "apps/email/message", name: "Message" },
  {
    path: CoreUiRoutePath.NotFoundError,
    name: "Page Not Found",
    component: CoreUiNotFoundError,
  },
  {
    path: CoreUiRoutePath.InternalServerError,
    name: "Internal Server Error",
    component: CoreUiInternalServerError,
  },
];

export default coreuiRoutes;
